import { ColDef, ColumnState, GridReadyEvent, IRowNode } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useForm } from '@mantine/form';
import { useDidUpdate, useLocalStorage, useScrollIntoView } from '@mantine/hooks';
import yasml from '@thirtytech/yasml';
import dayjs from 'dayjs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  BrokerNerdsBarkerCoreModelsInventoryAddListingRequest,
  BrokerNerdsBarkerCoreModelsInventoryAddListingSeat,
  BrokerNerdsBarkerCoreModelsInventoryEventBase,
  useGetApiCatalogEvents,
  useGetApiInventoryVendors,
  usePostApiInventoryListingsBulk,
} from '../../../api';
import { useGlobalState } from '../../../data/Global.state';
import { FlattenObjectKeys } from '../../../ts-utils';
import { delay } from '../../../utils/delay';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { DateFormats } from '../../../utils/globals';
import { useMemoDeep } from '../../../utils/use-memo-weak';
import { PurchaseOrder, PurchaseOrderGroup } from '../../../types';
import { useFlag } from '@unleash/proxy-client-react';

export const DEFAULT_ORDER: PurchaseOrder = {
  section: '',
  row: '',
  seats: [],
  ticketStockId: 'Unknown',
  unitCost: undefined,
  unitPrice: undefined,
  externalNotes: '',
  internalNotes: '',
  eventId: undefined,
  referenceNumber: '',
  vendorId: undefined,
  key: uuid(),
  quantity: undefined,
  oddEven: false,
  seatFrom: undefined,
  vendorProperties: {
    pointOfSaleId: 'VictoryLiveOne',
    purchaser: '',
    stockType: 'mobiletransfer',
    ccInfoCard: undefined,
  },
};

export type SummaryGridItem = BrokerNerdsBarkerCoreModelsInventoryEventBase &
  Omit<PurchaseOrder, 'vendorProperties'> & {
    // Flattened vendorProperties for grid columns
    purchaser?: string;
    stockType?: string;
    ccInfoCard?: string | null;
    tags?: string[] | null;
  };

function VL1PurchaseState({ initialEvent, onClose }: { initialEvent?: BrokerNerdsBarkerCoreModelsInventoryEventBase; onClose?: (bypass: boolean) => void }) {
  const isSelectEventsEnabled = !initialEvent;
  const catalogGridRef = useRef<AgGridReact<BrokerNerdsBarkerCoreModelsInventoryEventBase>>(null);
  const summaryGridRef = useRef<AgGridReact<SummaryGridItem>>(null);
  const { tenants, selectedTenantIds } = useGlobalState('tenants', 'selectedTenantIds');
  const [orderEvents, setOrderEvents] = useState<BrokerNerdsBarkerCoreModelsInventoryEventBase[]>(initialEvent ? [initialEvent] : []);
  const [selectedEvents, setSelectedEvents] = useState<BrokerNerdsBarkerCoreModelsInventoryEventBase[]>(initialEvent ? [initialEvent] : []);
  const [active, setActive] = useState(0);

  // If the user only has a single VL1 company selected, let's default to that account.
  const selectedVL1Tenants = tenants?.filter((t) => t.pointOfSaleId === 'VictoryLiveOne' && selectedTenantIds?.includes(t.tenantId));
  const singleVL1Tenant = selectedVL1Tenants?.length === 1 ? selectedVL1Tenants[0] : undefined;

  const hasCreatePoFlag = useFlag('vl1-create-po');

  const { data: vendors, isLoading: isVendorsLoading } = useGetApiInventoryVendors(
    {},
    {
      query: {
        enabled: !!singleVL1Tenant && hasCreatePoFlag,
        select(data) {
          return data.data;
        },
      },
    },
  );

  // const matchingDtiAccount = useMemo(() => dtiAccounts.find((x) => x.name === singleVL1Tenant?.name), [dtiAccounts, singleVL1Tenant?.name]);

  const [isVendorError, setIsVendorError] = useState(false);
  const [selectedVendorId, _setSelectedVendorId] = useState<string | undefined>(vendors?.length === 1 ? vendors[0].vendorId : undefined);
  const setSelectedVendorId = useCallback((id: string | undefined) => {
    setIsVendorError(false);
    _setSelectedVendorId(id);
  }, []);
  const nextStep = useCallback(() => {
    if (!selectedVendorId) {
      setIsVendorError(true);
    } else {
      setIsVendorError(false);
      setActive((current) => (current < 3 ? current + 1 : current));
    }
  }, [selectedVendorId]);

  const prevStep = useCallback(() => setActive((current) => (current > 0 ? current - 1 : current)), []);
  useDidUpdate(() => setSelectedVendorId(vendors && vendors?.length === 1 ? vendors[0].vendorId : undefined), [vendors]);
  const [activeListing, setActiveListing] = useState<{ groupIndex: number; index: number }>({ groupIndex: 0, index: 0 });
  const { scrollIntoView, targetRef, scrollableRef } = useScrollIntoView<HTMLDivElement, HTMLDivElement>({ offset: 0 });
  const form = useForm<{ groups: PurchaseOrderGroup[] }>({
    initialValues: {
      groups: [
        {
          eventIds: initialEvent ? [initialEvent.eventId] : [],
          orders: [DEFAULT_ORDER],
          group: uuid(),
        },
      ],
    },
    validate: (values) => {
      const errors: Partial<Record<FlattenObjectKeys<typeof values>, string>> = {};
      // const eventOrderHashes = new Set<string>();
      values.groups.forEach((group, groupIndex) => {
        group.orders.forEach((order, index) => {
          // if (!order.section && !order.row && !order.seat_from && !order.quantity) {
          //   return;
          // }
          // if (typeof order.unitPrice !== 'number') {
          //   errors[`groups.${groupIndex}.orders.${index}.unitPrice`] = 'Price is required';
          // }
          if (!order.section) {
            errors[`groups.${groupIndex}.orders.${index}.section`] = 'Section is required';
          }
          if (!order.row) {
            errors[`groups.${groupIndex}.orders.${index}.row`] = 'Row is required';
          }
          if (!order.seats.length) {
            errors[`groups.${groupIndex}.orders.${index}.seatFrom`] = 'Seat From is required';
          }
          if (!order.quantity) {
            errors[`groups.${groupIndex}.orders.${index}.quantity`] = 'Quantity is required';
          }
          // group.eventIds.forEach((eventId) => {
          //   const eventOrderHash = `${eventId}-${order.section}-${order.row}-${order.seat_from}`;
          //   if (eventOrderHashes.has(eventOrderHash)) {
          //     errors[`groups.${groupIndex}.orders.${index}.section`] = 'Duplicate section, row, and seat in another listing';
          //     errors[`groups.${groupIndex}.orders.${index}.row`] = 'Duplicate section, row, and seat in another listing';
          //     errors[`groups.${groupIndex}.orders.${index}.seat_from`] = 'Duplicate section, row, and seat in another listing';
          //   } else {
          //     eventOrderHashes.add(eventOrderHash);
          //   }
          // });
        });
      });
      return errors;
    },
  });
  const formValues = useMemoDeep(() => form.values, [form.values]);

  useDidUpdate(() => {
    if (form.values.groups.length === 1 && form.values.groups[0].orders[0].section === '') {
      form.setFieldValue(
        'groups.0.eventIds',
        selectedEvents.map((x) => x.eventId),
      );
    }
  }, [selectedEvents]);

  useDidUpdate(() => {
    const formEventIds = formValues.groups.flatMap((group) => group.eventIds);
    setOrderEvents((event) => event.filter((x) => formEventIds.includes(x.eventId)).concat(selectedEvents));
  }, [selectedEvents, formValues.groups.length]);

  useDidUpdate(() => {
    formValues.groups.forEach((group, groupIndex) => {
      group.orders.forEach((order, index) => {
        if (order.quantity !== undefined && order.seatFrom && order.seats) {
          const updatedOrder = { ...order };
          updatedOrder.seats = Array.from({ length: order.quantity }, (_, i) => {
            const expectedSeatNumber = Number(order.seatFrom) + (order.oddEven ? i * 2 : i);

            return order.seats[i] !== undefined && order.seats[i].seatNumber === expectedSeatNumber ? order.seats[i] : { seatNumber: expectedSeatNumber };
          });
          form.setFieldValue(`groups.${groupIndex}.orders.${index}`, updatedOrder);
        }
      });
    });
  }, [formValues]);

  const { mutateAsync: bulkPurchase } = usePostApiInventoryListingsBulk({
    axios: {
      headers: {
        'x-tenant-id': selectedTenantIds[0],
      },
    },
  });

  const [isSaving, setIsSaving] = useState(false);

  const onSubmit = useCallback(
    async (values: typeof formValues) => {
      setIsSaving(true);
      if (selectedVendorId) {
        const flattenedOrders = values.groups.flatMap((group) =>
          group.eventIds.flatMap((eventId) =>
            group.orders.flatMap(
              (order) =>
                ({
                  // TODO: Fix nullabilty and remove type assertion
                  eventId,
                  referenceNumber: order.referenceNumber,
                  unitCost: order.unitCost!,
                  unitPrice: order.unitPrice!,
                  section: order.section,
                  row: order.row,
                  internalNotes: order.internalNotes,
                  externalNotes: order.externalNotes,
                  vendorId: selectedVendorId,
                  ticketStockId: order.ticketStockId,
                  inHandDate: order.inHandDate,
                  seats: order.seats,
                  vendorProperties: order.vendorProperties,
                }) satisfies BrokerNerdsBarkerCoreModelsInventoryAddListingRequest,
            ),
          ),
        );

        await bulkPurchase({
          data: flattenedOrders,
        });
      }

      await delay(5 * 1000); // HACK: This is to give the API time to process the request before closing the modal.
      onClose?.(true);
      setIsSaving(false);
    },
    [bulkPurchase, onClose, selectedVendorId],
  );

  type SearchForm = {
    fromDate: Date | null;
    toDate: Date | null;
    query: string | undefined;
  };

  type FiltersForm = {
    excludeParking: boolean;
  };

  const searchForm = useForm<SearchForm>({
    mode: 'uncontrolled',
    initialValues: {
      fromDate: dayjs().startOf('day').toDate(),
      toDate: dayjs().endOf('day').toDate(),
      query: '',
    },
    validate: {
      fromDate: (value) => {
        if (!value) {
          return 'From Date is required';
        }
        return undefined;
      },
      toDate: (value, values) => {
        if (!value) {
          return 'To Date is required';
        }
        if (value && values.fromDate && dayjs(value).isBefore(dayjs(values.fromDate))) {
          return 'To Date must be after From Date';
        }
        return undefined;
      },
    },
  });
  const filtersForm = useForm<FiltersForm>({
    initialValues: {
      excludeParking: true,
    },
  });

  const [submittedSearchValues, setSubmittedSearchValues] = useState<SearchForm | null>(null);

  const { data: _eventSearchResults, isFetching: isCatalogLoading } = useGetApiCatalogEvents(
    {
      fromDate: submittedSearchValues?.fromDate!,
      toDate: submittedSearchValues?.toDate!,
      query: submittedSearchValues?.query,
    },
    {
      query: {
        staleTime: import.meta.env.DEV ? 1000 * 60 * 30 : undefined,
        gcTime: import.meta.env.DEV ? 1000 * 60 * 30 : undefined,
        enabled: !!submittedSearchValues,
        select(data) {
          return data.data;
        },
      },
    },
  );

  const eventSearchResults = useMemo(
    () =>
      _eventSearchResults?.filter((x) => {
        const { excludeParking } = filtersForm.values;

        if (excludeParking && x.name.toLowerCase().includes('parking')) {
          return false;
        }

        return true;
      }) ?? [],
    [_eventSearchResults, filtersForm.values],
  );

  if (isCatalogLoading) {
    // NOTE: This was added as prop finally in the newer version of AG-Grid. Remove when upgraded.
    catalogGridRef.current?.api.showLoadingOverlay();
  }

  useDidUpdate(() => {
    if (!isCatalogLoading) {
      // NOTE: This was added as prop finally in the newer version of AG-Grid. Remove when upgraded.
      catalogGridRef.current?.api.hideOverlay();
    }
  }, [isCatalogLoading]);

  const onSearchSubmit = useCallback((values: SearchForm) => {
    setSubmittedSearchValues(values);
  }, []);

  type CatalogEventColumn = ColDef<BrokerNerdsBarkerCoreModelsInventoryEventBase> & {
    exclude?: boolean;
    allowSorting?: boolean;
    sort?: 'asc' | 'desc' | null;
    sortIndex?: number | null;
  };
  const defaultCatalogDefs: CatalogEventColumn[] = useMemo(
    () =>
      [
        { headerName: 'Name', field: 'name', flex: 1, minWidth: 260, sort: undefined },
        {
          headerName: 'Date',
          field: 'localDateTime',
          sortIndex: 0,
          sort: 'asc',
          valueGetter: (params) => (params.data?.localDateTime ? formatDate(dayjs(params.data.localDateTime).toDate(), DateFormats.GridFullWithDOW) : null),
          cellStyle: { fontVariantNumeric: 'tabular-nums', letterSpacing: -0.65 },
        },
        { headerName: 'Venue', field: 'venue.name', minWidth: 200 },
        { headerName: 'City', field: 'venue.city', minWidth: 150 },
        { headerName: 'State', field: 'venue.state', minWidth: 100 },
        { headerName: 'Performer', field: 'performer.name', minWidth: 150 },
        { headerName: 'Event ID', colId: 'eventId', field: 'eventId', minWidth: 100 },
      ] as CatalogEventColumn[],
    [],
  );

  type SummaryColumn = ColDef<SummaryGridItem> & {
    exclude?: boolean;
    allowSorting?: boolean;
    sort?: 'asc' | 'desc' | null;
    sortIndex?: number | null;
  };
  const defaultSummaryDefs: SummaryColumn[] = useMemo(
    () =>
      [
        { colId: 'eventId', field: 'eventId', exclude: true, allowSorting: false, hide: true },
        { headerName: 'Name', field: 'name', flex: 1, minWidth: 260, sort: undefined, hide: true },
        {
          headerName: 'Date',
          field: 'localDateTime',
          hide: true,
          sortIndex: 0,
          sort: 'asc',
          valueGetter: (params) => (params.data?.localDateTime ? formatDate(dayjs(params.data.localDateTime).toDate(), DateFormats.GridFull) : null),
          cellStyle: { fontVariantNumeric: 'tabular-nums', letterSpacing: -0.65 },
        },
        { headerName: 'Performer', field: 'performer.name', minWidth: 150, hide: true },
        { headerName: 'Venue', field: 'venue.name', minWidth: 200, hide: true },
        { headerName: 'Section', field: 'section', minWidth: 80, width: 160, sort: 'asc', sortIndex: 1 },
        {
          headerName: 'Row',
          field: 'row',
          minWidth: 70,
          width: 70,
          cellStyle: {
            textAlign: 'center',
          },
        },
        {
          headerName: 'Seats',
          colId: 'seats',
          minWidth: 90,
          width: 90,
          cellStyle: {
            textAlign: 'center',
          },
          valueGetter: (params) => {
            if (params.data) {
              return calculateSeatRange(params.data.seats);
            }
            return null;
          },
        },
        { headerName: 'Qty', minWidth: 70, width: 70, sort: 'desc', sortIndex: 2, valueGetter: (params) => params.data?.seats.length },
        {
          headerName: 'Cost',
          field: 'unitCost',
          minWidth: 110,
          width: 110,
          cellStyle: {
            textAlign: 'right',
          },
          valueFormatter: (params) => formatCurrency(params.value),
        },
        {
          headerName: 'Price',
          field: 'unitPrice',
          minWidth: 110,
          width: 110,
          cellStyle: {
            textAlign: 'right',
          },
          valueFormatter: (params) => formatCurrency(params.value),
        },
        // { headerName: 'stock', field: 'stock', minWidth: 130, width: 130, valueGetter: (params) => stock_options.find((x) => x.value === params.data?.stock?.toString())?.label },
        { headerName: 'Internal Notes', field: 'internalNotes', minWidth: 120, hide: false },
        { headerName: 'External Notes', field: 'externalNotes', minWidth: 120, hide: false },
        { headerName: 'Order Number', field: 'referenceNumber', minWidth: 120, width: 120, hide: false },
        { headerName: 'Purchaser', field: 'purchaser', minWidth: 120, width: 180, hide: false },
        { headerName: 'CC #', field: 'ccInfoCard', minWidth: 60, width: 100, hide: false },
      ] as SummaryColumn[],
    [],
  );

  const [eventGridState] = useLocalStorage<ColumnState[]>({
    key: 'catalog-grid-event-state',
    getInitialValueInEffect: false,
  });

  const catalogColumnDefs: ColDef<BrokerNerdsBarkerCoreModelsInventoryEventBase>[] = useMemo(
    () =>
      defaultCatalogDefs
        .map((col) => {
          // Clears properties before giving it to AG-Grid to avoid warnings.
          const { allowSorting: _allowSorting, exclude: _exclude, ..._col } = col;
          return { ..._col } as ColDef<BrokerNerdsBarkerCoreModelsInventoryEventBase>;
        })
        .sort((a, b) => {
          const aIndex = eventGridState?.findIndex((x) => x.colId === a.colId);
          const bIndex = eventGridState?.findIndex((x) => x.colId === b.colId);
          return (aIndex ?? 0) - (bIndex ?? 0);
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const summaryColumnDefs: ColDef<SummaryGridItem>[] = useMemo(
    () =>
      defaultSummaryDefs
        .map((col) => {
          // Clears properties before giving it to AG-Grid to avoid warnings.
          const { allowSorting: _allowSorting, exclude: _exclude, ..._col } = col;
          if (_col.field === 'eventId') {
            _col.rowGroup = true;
            _col.hide = true;
          }
          return { ..._col } as ColDef<SummaryGridItem>;
        })
        .sort((a, b) => {
          const aIndex = eventGridState?.findIndex((x) => x.colId === a.colId);
          const bIndex = eventGridState?.findIndex((x) => x.colId === b.colId);
          return (aIndex ?? 0) - (bIndex ?? 0);
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onCatalogGridReady = useCallback(
    (event: GridReadyEvent) => {
      if (selectedEvents) {
        event.api.forEachNode((node: IRowNode<BrokerNerdsBarkerCoreModelsInventoryEventBase>) => {
          if (selectedEvents.some((x) => x.eventId === node.data?.eventId)) {
            node.setSelected(true);
          }
        });
      }
    },
    [selectedEvents],
  );

  const removeCatalogEvent = useCallback((event: BrokerNerdsBarkerCoreModelsInventoryEventBase) => {
    setSelectedEvents((s) => {
      if (s.includes(event)) {
        s.splice(s.indexOf(event), 1);
      } else {
        s.push(event);
      }
      // Update api-grid selections before returning
      catalogGridRef.current?.api.forEachNode((node) => {
        if (s.some((x) => x.eventId === node.data?.eventId)) {
          node.setSelected(true);
        } else {
          node.setSelected(false);
        }
      });
      return [...s];
    });
  }, []);

  const addTicketGroup = useCallback(() => {
    const { hasErrors } = form.validate();
    const order = form.values.groups[activeListing.groupIndex]?.orders[activeListing.index];
    const isEmptyRecord = order && !order.section && !order.row && order.seats.length === 0;

    if (!hasErrors || isEmptyRecord) {
      if (isEmptyRecord) {
        if (form.values.groups[activeListing.groupIndex].orders.length === 1) {
          form.removeListItem('groups', activeListing.groupIndex);
        } else {
          form.removeListItem(`groups.${activeListing.groupIndex}.orders`, activeListing.index);
        }
      }
      form.insertListItem('groups', {
        orders: [{ ...structuredClone(DEFAULT_ORDER), key: uuid() } satisfies PurchaseOrder],
        eventIds: selectedEvents.map((x) => x.eventId),
        group: uuid(),
      } satisfies PurchaseOrderGroup);
      setActiveListing({ groupIndex: form.values.groups.length + (isEmptyRecord && form.values.groups[activeListing.groupIndex].orders.length === 1 ? -1 : 0), index: 0 });
    }
  }, [activeListing.groupIndex, activeListing.index, form, selectedEvents]);

  const addListing = useCallback(
    (groupIndex: number, tail?: boolean) => {
      const { hasErrors } = form.validate();
      console.log('hasErrors', form.validate());
      const order = form.values.groups[activeListing.groupIndex]?.orders[activeListing.index];
      const isEmptyRecord = order && !order.section && !order.row && order.seats.length === 0;
      if (!hasErrors && !isEmptyRecord) {
        form.insertListItem(`groups.${groupIndex}.orders`, { ...structuredClone(DEFAULT_ORDER), key: uuid() } satisfies PurchaseOrder, tail ? undefined : 0);
        setActiveListing({ groupIndex, index: tail ? form.values.groups[groupIndex].orders.length : 0 });
      }
    },
    [activeListing.groupIndex, activeListing.index, form],
  );

  useDidUpdate(() => {
    scrollIntoView({ alignment: 'center' });
  }, [activeListing.groupIndex, activeListing.index]);

  const copyListing = useCallback(
    (groupIndex: number, index: number) => {
      const { hasErrors } = form.validate();
      if (!hasErrors) {
        const listing = form.values.groups[groupIndex].orders[index];
        form.insertListItem(`groups.${groupIndex}.orders`, { ...structuredClone(listing), key: uuid() } satisfies PurchaseOrder, index);
        setActiveListing({ groupIndex, index: index + 1 });
      }
    },
    [form],
  );

  const mergedEventsWithInventory: SummaryGridItem[] = useMemo(() => {
    const distinctEventIds = new Set(formValues.groups.flatMap((x) => x.eventIds));
    // Precompute eventId -> event so the per-eventId lookup is O(1) instead of orderEvents.find per id.
    const eventById = new Map(orderEvents.map((event) => [event.eventId, event]));
    return Array.from(distinctEventIds).flatMap((eventId) => {
      const event = eventById.get(eventId)!;
      return (
        formValues.groups
          .filter((x) => x.eventIds.includes(eventId))
          .flatMap((x) => x.orders)
          // Collapse the two sequential .filter passes into one (same predicate conjunction, same order).
          .filter((x) => x.section && x.vendorProperties?.pointOfSaleId === 'VictoryLiveOne')
          .map((order) => {
            // pointOfSaleId is already known to be 'VictoryLiveOne' from the filter above; read props directly.
            const vendorProperties = order.vendorProperties?.pointOfSaleId === 'VictoryLiveOne' ? order.vendorProperties : undefined;
            return {
              ...order,
              ...event,
              eventId: event.eventId,
              purchaser: vendorProperties?.purchaser,
              stockType: vendorProperties?.stockType,
              ccInfoCard: vendorProperties?.ccInfoCard,
            };
          })
      );
    });
  }, [formValues.groups, orderEvents]);

  const totalCost = useMemo(
    () =>
      formValues.groups.reduce(
        (totalAcc, group) => totalAcc + group.orders.reduce((groupAcc, order) => groupAcc + (order.unitCost || 0) * (order.seats.length || 0) * group.eventIds.length, 0),
        0,
      ),
    [formValues],
  );
  const totalTickets = useMemo(
    () => formValues.groups.reduce((totalAcc, group) => totalAcc + group.orders.reduce((groupAcc, order) => groupAcc + (order.seats.length || 0) * group.eventIds.length, 0), 0),
    [formValues],
  );
  const totalListings = useMemo(() => formValues.groups.flatMap((x) => x.orders).length * selectedEvents.length, [formValues, selectedEvents]);

  return {
    form,
    searchForm,
    onSubmit,
    DEFAULT_ORDER,
    eventSearchResults,
    selectedEvents,
    setSelectedEvents,
    catalogGridRef,
    summaryGridRef,
    catalogColumnDefs,
    summaryColumnDefs,
    onCatalogGridReady,
    mergedEventsWithInventory,
    totalCost,
    totalListings,
    setActive,
    active,
    nextStep,
    prevStep,
    totalTickets,
    addTicketGroup,
    addListing,
    copyListing,
    orderEvents,
    selectedVendorId,
    setSelectedVendorId,
    activeListing,
    setActiveListing,
    removeCatalogEvent,
    calculateSeatRange,
    onSearchSubmit,
    isCatalogLoading,
    targetRef,
    scrollableRef,
    filtersForm,
    isSelectEventsEnabled,
    isSaving,
    isVendorError,
    vendors,
    isVendorsLoading,
  };
}

export const { Provider: VL1PurchaseStateProvider, useSelector: usePurchaseState } = yasml(VL1PurchaseState);

export const disclosures_options = [
  { value: '18+ Event', label: '18+ Event' },
  { value: '21+ Event', label: '21+ Event' },
  { value: 'ADA Wheelchair Accessible', label: 'ADA Wheelchair Accessible' },
  { value: 'Alcohol-Free Seating', label: 'Alcohol-Free Seating' },
  { value: 'Child Ticket', label: 'Child Ticket' },
  { value: 'Limited View (printed on ticket)', label: 'Limited View (printed on ticket)' },
  {
    value: 'MOBILE ENTRY. Scan your tickets from your phone for this event. Do not print these tickets. MOBILE QR',
    label: 'MOBILE ENTRY. Scan your tickets from your phone for this event. Do not print these tickets. MOBILE QR',
  },
  { value: 'Obstructed View (printed on ticket)', label: 'Obstructed View (printed on ticket)' },
  { value: 'Parking Pass Only', label: 'Parking Pass Only' },
  { value: 'Partial View (printed on ticket)', label: 'Partial View (printed on ticket)' },
  { value: 'Piggyback Seats. Same seat number in back to back rows.', label: 'Piggyback Seats. Same seat number in back to back rows.' },
  { value: 'Restricted View (printed on ticket)', label: 'Restricted View (printed on ticket)' },
  { value: 'Side Stage (printed on ticket)', label: 'Side Stage (printed on ticket)' },
  { value: 'Side View (printed on ticket)', label: 'Side View (printed on ticket)' },
  { value: 'Standing Room Only', label: 'Standing Room Only' },
  {
    value: 'Xfer MOBILE ENTRY. Scan your tickets from your phone for this event. Do not print these tickets',
    label: 'Xfer MOBILE ENTRY. Scan your tickets from your phone for this event. Do not print these tickets',
  },
];
export const stock_options = [
  { value: 'mobiletransfer', label: 'Mobile Transfer' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'eticket', label: 'E-Ticket' },
  { value: 'flash', label: 'Flash' },
  { value: 'hard', label: 'Hard' },
  { value: 'paperless', label: 'Paperless' },
  { value: 'willcall', label: 'Will Call' },
  { value: 'dropship', label: 'Dropship' },
];

export const mapStringToDisclosures = (disclosures: string): { disclosures: string[]; additionalText: string } => {
  let _disclosures = disclosures;
  const foundDisclosures: string[] = [];
  disclosures_options.forEach(({ value }) => {
    if (disclosures.includes(value)) {
      _disclosures = _disclosures.replace(value, '');
      foundDisclosures.push(value);
    }
  });
  return { disclosures: foundDisclosures, additionalText: _disclosures.trim() };
};

export const calculateSeatRange = (seats: BrokerNerdsBarkerCoreModelsInventoryAddListingSeat[]): string => {
  if (seats === undefined || seats.length === 0) {
    return '';
  }

  if (seats.length === 1) {
    return seats[0].seatNumber.toString();
  }

  const sortedSeats = seats.sort((a, b) => a.seatNumber - b.seatNumber);
  const seatFrom = sortedSeats[0].seatNumber;
  const endSeat = sortedSeats[sortedSeats.length - 1].seatNumber;
  return `${seatFrom}-${endSeat}`;
};
